import React from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Typography } from '@telus-uds/components-web';
import {
  FormGroup, AccordionHeader, ToggleButton, AccordionPanel, Chevron, HeaderSpacer, Badge,
  Table, Th, Tr, GroupCell, Meta
} from './css';
import SectionRow from './sectionRow';
import { isTerminal, isPending } from './useAutomationRun';

const UNGROUPED = 'Scenarios';
const COLUMN_COUNT = 5;

const groupSections = (sections) => sections.reduce((groups, section) => {
  const key = section.group || UNGROUPED;
  const existing = groups.find((group) => group.key === key);
  if (existing) existing.sections.push(section);
  else groups.push({ key, sections: [section] });
  return groups;
}, []);

const summarise = (sections, runs) => sections.reduce((counts, section) => {
  const run = runs[section.id];
  if (!run || isPending(run.status)) return counts;
  if (!isTerminal(run.status)) return { ...counts, running: counts.running + 1 };
  if (run.status === 'passed') return { ...counts, passed: counts.passed + 1 };
  return { ...counts, failed: counts.failed + 1 };
}, { passed: 0, failed: 0, running: 0 });

const FormAccordion = ({
  form, runs, frozen, batchActive, isBatchOwner, startingSectionId, open, onToggle, onRun,
  selectedIds, onToggleSelect, onSetSelected, onRunSelected
}) => {
  const sections = form.sections || [];
  const counts = summarise(sections, runs);
  const panelId = `automation-panel-${form.id}`;
  const sectionIds = sections.map((section) => section.id);
  const selectedCount = sectionIds.filter((id) => selectedIds.has(id)).length;

  const allSelected = sections.length > 0 && selectedCount === sections.length;

  const toggleSelectAll = () => onSetSelected(sectionIds, !allSelected);

  const handleRunSelected = () => {
    const ids = sectionIds.filter((id) => selectedIds.has(id));
    if (!ids.length) return;
    onRunSelected(ids);
  };

  return (
    <FormGroup>
      <AccordionHeader open={open}>
        <ToggleButton
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => onToggle(form.id)}
        >
          <Chevron open={open} aria-hidden="true">▶</Chevron>
          <Typography variant={{ bold: true, size: 'large' }}>
            {`${form.shortLabel || form.label} Automation Test Cases`}
          </Typography>
        </ToggleButton>

        {sections.length > 0 && (
          <Checkbox
            id={`select-all-${form.id}`}
            name={`select-all-${form.id}`}
            label="Select all"
            checked={allSelected}
            inactive={frozen || batchActive}
            onChange={toggleSelectAll}
          />
        )}
        <Button
          variant={{ priority: 'high' }}
          type="button"
          onClick={handleRunSelected}
          inactive={frozen || batchActive || selectedCount === 0}
        >
          {isBatchOwner ? 'Running selected…' : `Run selected (${selectedCount})`}
        </Button>

        <HeaderSpacer />
        <Badge color="#666" background="#f0f0f0">{`${sections.length} cases`}</Badge>
        {counts.running > 0 && <Badge color="#00437d" background="#e5eff8">Running</Badge>}
        {counts.passed > 0 && <Badge color="#2b8000" background="#e9f5e2">{`${counts.passed} passed`}</Badge>}
        {counts.failed > 0 && <Badge color="#772525" background="#fbeaea">{`${counts.failed} failed`}</Badge>}
      </AccordionHeader>

      {open && (
        <AccordionPanel id={panelId}>
          {sections.length === 0 ? (
            <Meta style={{ margin: '1rem' }}>No test cases are wired up for this form yet.</Meta>
          ) : (
            <Table>
              <colgroup>
                <col style={{ width: '2.5rem' }} />
                <col />
                <col style={{ width: '9rem' }} />
                <col style={{ width: '11rem' }} />
                <col style={{ width: '14rem' }} />
              </colgroup>
              <thead>
                <tr>
                  <Th aria-hidden="true" />
                  <Th>Test case</Th>
                  <Th>Approx. duration</Th>
                  <Th>Status</Th>
                  <Th style={{ textAlign: 'right' }}>Action</Th>
                </tr>
              </thead>
              {groupSections(sections).map((group) => (
                <tbody key={group.key}>
                  <Tr>
                    <GroupCell colSpan={COLUMN_COUNT}>{group.key}</GroupCell>
                  </Tr>
                  {group.sections.map((section) => (
                    <SectionRow
                      key={section.id}
                      section={section}
                      run={runs[section.id] || null}
                      frozen={frozen}
                      starting={startingSectionId === section.id}
                      selected={selectedIds.has(section.id)}
                      queued={isPending((runs[section.id] || {}).status)}
                      onToggleSelect={onToggleSelect}
                      onRun={onRun}
                    />
                  ))}
                </tbody>
              ))}
            </Table>
          )}
        </AccordionPanel>
      )}
    </FormGroup>
  );
};

const sectionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  group: PropTypes.string,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  estimatedMs: PropTypes.number,
  mutatesData: PropTypes.bool
});

const runShape = PropTypes.shape({
  runId: PropTypes.string,
  sectionId: PropTypes.string,
  status: PropTypes.string,
  dispatchedAt: PropTypes.string,
  durationMs: PropTypes.number,
  batchId: PropTypes.string,
  triggeredByName: PropTypes.string
});

FormAccordion.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    shortLabel: PropTypes.string,
    sections: PropTypes.arrayOf(sectionShape)
  }).isRequired,
  runs: PropTypes.objectOf(runShape).isRequired,
  frozen: PropTypes.bool,
  batchActive: PropTypes.bool,
  isBatchOwner: PropTypes.bool,
  startingSectionId: PropTypes.string,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onSetSelected: PropTypes.func.isRequired,
  onRunSelected: PropTypes.func.isRequired
};

FormAccordion.defaultProps = {
  frozen: false,
  batchActive: false,
  isBatchOwner: false,
  startingSectionId: null,
  open: false
};

export default FormAccordion;
